/* Hallmark · component: auth-aside · genre: modern-minimal · theme: project system
 * (Outfit display · Geist body · indigo accent — preserved from src/index.css)
 * states: n/a — non-interactive panel
 * pre-emit critique: P4 H5 E4 S5 R5 V4
 */

/**
 * AuthBrandPanel
 *
 * Left half of the split-screen auth layout. core-frontend's AuthLayout renders
 * `config.layout.authAside` as a `lg:w-1/2` column and hides it below `lg`, so
 * the form keeps the whole screen on mobile. Same wiring hausverwaltung uses.
 *
 * Everything on this panel is verifiable: the endpoint is the app's real PDF
 * generation route (`POST /api/v1/pdf/generate`) and the use-cases below are
 * document kinds the template editor + API actually produce. No counters, no
 * "trusted by N teams" — an invented number on a login screen is the fastest
 * way to look generated, and we have no real one to show here.
 */

import { useTranslation } from 'react-i18next'
import { PdfApiLogo } from './PdfApiLogo'

/** Document kinds the API + template editor produce today. */
const USE_CASES = ['Reports', 'Letters', 'Certificates', 'Quotes', 'Filled forms'] as const

export function AuthBrandPanel() {
  const { t } = useTranslation()

  return (
    <div className="flex w-full flex-col justify-between gap-16 px-10 py-14 xl:px-16">
      <PdfApiLogo size={32} />

      <div className="flex flex-col gap-6">
        {/*
          Left-aligned and roman. The tight tracking is the landing's DESIGN.md
          rule ("Tight Display Type", -0.04em); the emphasis sits on the accent
          word, not on an italic — italic headers are a banned tell.
        */}
        <h2 className="max-w-[18ch] font-heading text-4xl leading-[1.1] tracking-[-0.04em] xl:text-5xl">
          {t('authPanel.title')}
        </h2>
        <p className="max-w-[46ch] text-base leading-relaxed text-muted-foreground">
          {t('authPanel.description')}
        </p>
      </div>

      <div className="flex flex-col gap-6">
        {/*
          A typographic frame — label, hairline, <pre>. Explicitly not a faked
          browser/editor window with traffic-light dots: the reader already has
          real chrome, and a drawn one is a tell.
        */}
        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">{t('authPanel.endpointLabel')}</span>
          <div className="border-t pt-3">
            <pre className="overflow-x-auto font-mono text-xs text-foreground/80">
              <code>
                <span className="text-primary">POST</span> /api/v1/pdf/generate
              </code>
            </pre>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">{t('authPanel.formatsLabel')}</span>
          <ul className="flex flex-wrap gap-x-3 gap-y-1.5 text-sm text-foreground/70">
            {USE_CASES.map((useCase) => (
              <li key={useCase} className="font-mono text-xs">
                {useCase}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
